import React, { useCallback, useEffect, useState } from "react";
import { CONVERSION_WORDS, FLAG_WORDS, listApplications, qty } from "../../Operations/Inventory/useChemicalsApi";
import { shortDate } from "../customerFormat";
import { TabNote, TabState } from "./tabShared";

// Every product applied at this property, as FieldRoutes recorded it: what was logged,
// what that is in concentrate (and how it was converted), rate per 1,000 sq ft, flags.
export default function ChemicalsTab({ record }) {
  const customerPk = record?.object !== "lead" ? record?.raw?.id : null;
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState("loading");

  const load = useCallback(async (p) => {
    const data = await listApplications({ customer: customerPk, page: p, page_size: 50 });
    return data;
  }, [customerPk]);

  useEffect(() => {
    if (!customerPk) { setState("none"); return undefined; }
    let alive = true;
    setState("loading");
    load(1).then((d) => {
      if (!alive) return;
      setRows(d.results); setCount(d.count); setPage(1); setState("ok");
    }).catch(() => alive && setState("error"));
    return () => { alive = false; };
  }, [customerPk, load]);

  const more = async () => {
    const d = await load(page + 1);
    setRows((r) => [...r, ...d.results]); setPage(page + 1);
  };

  if (state === "none") return <TabState>Chemical history is kept on customer records.</TabState>;
  if (state === "loading") return <TabState>Loading chemical history…</TabState>;
  if (state === "error") return <TabState>Couldn't load chemical history. Try again in a moment.</TabState>;
  if (!rows.length) {
    return <TabState>No chemical applications recorded for this property since October 2025.</TabState>;
  }
  const sizes = rows.map((r) => r.area_sqft).filter(Boolean);

  return (
    <div className="ct-stack">
      <TabNote>
        From FieldRoutes, read-only. {sizes.length ? `Property size used: ${Math.max(...sizes).toLocaleString()} sq ft.` : "No property size on file, so rates per 1,000 sq ft can't be checked."}
      </TabNote>
      <section className="cov-card">
        <div className="cov-card__head">
          <span>Applications</span>
          <span className="cov-card__count">{count}</span>
        </div>
        <div className="cov-card__body">
          {rows.map((a) => (
            <div key={a.id} className="cov-row">
              <div className="cov-row__main ct-row-main-wide">
                <span className="cov-row__title">{a.product || "Unknown product"}{a.epa_reg_number ? ` · EPA ${a.epa_reg_number}` : ""}</span>
                <span className="cov-row__meta">
                  {[shortDate(a.applied_date), a.service_type, a.tech].filter(Boolean).join(" · ")}
                </span>
                <span className="cov-row__meta">
                  {a.qty ? `Logged ${qty(a.qty, a.unit)}` : "No amount logged"}
                  {a.concentrate_qty != null ? ` → ${qty(a.concentrate_qty, a.concentrate_unit)} of product` : ""}
                  {a.rate_per_1000 != null ? ` · ${qty(a.rate_per_1000, a.concentrate_unit)} per 1,000 sq ft` : ""}
                </span>
                <span className="ct-note">{CONVERSION_WORDS[a.conversion] || a.conversion}{a.deleted_in_fr ? " · Deleted in FieldRoutes (deduct reversed)" : ""}</span>
              </div>
              <div className="cov-row__side">
                {a.flags.filter((f) => f.status === "open" && f.kind !== "size_unknown").map((f) => (
                  <span key={f.kind} className={`cov-pill ${f.severity === "compliance" ? "cov-pill--accent" : "cov-pill--muted"}`}>
                    {FLAG_WORDS[f.kind] || f.kind}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {rows.length < count ? <button type="button" className="ct-more" onClick={more}>Show older applications</button> : null}
        </div>
      </section>
    </div>
  );
}
